import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ChevronLeft, ChevronRight, MapPin, Pause, Play } from "lucide-react";
import { HERO_SLIDES } from "@/lib/heroSlides";

const SLIDE_MS = 6500;
const N = HERO_SLIDES.length;
const srcOf = (id, w) => `/media/hero/${id}-${w}.jpg`;

const Ctrl = ({ onClick, label, testId, children }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
    className="grid h-9 w-9 place-items-center rounded-full border border-white/40 bg-black/20 text-white backdrop-blur transition-colors hover:bg-white hover:text-ink">
    {children}
  </button>
);

/** Photo slide (only loads once it is current or next). The current and outgoing slides keep their slow zoom so the crossfade never jumps. */
const Slide = ({ s, active, zooming, load, first }) => (
  <div className={`absolute inset-0 transition-opacity ease-out [transition-duration:1400ms] ${active ? "opacity-100" : "opacity-0"}`} aria-hidden={!active} data-testid={`hero-slide-${s.id}`}>
    {load && (
      <img src={srcOf(s.id, 2560)} srcSet={`${srcOf(s.id, 1280)} 1280w, ${srcOf(s.id, 2560)} 2560w`} sizes="100vw" alt={s.alt}
        fetchpriority={first ? "high" : "auto"} style={{ objectPosition: s.pos }}
        className={`h-full w-full object-cover ${zooming ? "hero-kenburns" : ""}`} />
    )}
  </div>
);

/** Full-bleed carousel of real Hampton Roads bases, beaches and landmarks, with caption + controls along the bottom. */
export const HeroSlides = () => {
  const [[i, prev], setPair] = useState([0, null]);
  const [playing, setPlaying] = useState(true);
  const [loaded, setLoaded] = useState(() => new Set([0, 1]));
  const go = useCallback((j) => {
    const k = (j + N) % N;
    setLoaded((s) => new Set([...s, k, (k + 1) % N]));
    setPair(([cur]) => (cur === k ? [cur, null] : [k, cur]));
  }, []);
  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => go(i + 1), SLIDE_MS);
    return () => clearTimeout(t);
  }, [i, playing, go]);
  const s = HERO_SLIDES[i];
  const toExplorer = () => document.getElementById("explorer")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <div className="absolute inset-0 bg-neutral-900" data-testid="hero-slides">
        {HERO_SLIDES.map((x, k) => (
          <Slide key={x.id} s={x} active={k === i} zooming={k === i || k === prev} load={loaded.has(k)} first={k === 0} />
        ))}
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/75" />
      <div className="absolute inset-x-0 bottom-[76px] z-20 mx-auto flex max-w-[1440px] items-end justify-between gap-4 pb-4 pl-5 pr-[92px] sm:pl-8 lg:bottom-0 lg:pb-6 lg:pr-[112px]">
        <div key={s.id} className="min-w-0 animate-rise-in" data-testid="hero-slide-caption">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-brass" /> <span className="truncate">{s.place}</span>
          </p>
          <p className="mt-1 text-[13px] text-white/85">{s.note} <span className="text-[12px] text-white/70">· {s.credit}</span></p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Ctrl onClick={() => go(i - 1)} label="Previous photo" testId="hero-slide-prev"><ChevronLeft className="h-4 w-4" /></Ctrl>
          <Ctrl onClick={() => setPlaying((p) => !p)} label={playing ? "Pause slideshow" : "Play slideshow"} testId="hero-slide-toggle">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Ctrl>
          <Ctrl onClick={() => go(i + 1)} label="Next photo" testId="hero-slide-next"><ChevronRight className="h-4 w-4" /></Ctrl>
          <button type="button" onClick={toExplorer} data-testid="hero-explore-bases"
            className="ml-2 hidden items-center gap-2 text-[14px] text-white hover:underline md:flex">
            Every base in 3D <ArrowDown className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
};
